import { useState, useEffect } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { celestialCreamLow as p } from "@/data/celestialCreamLow";
import { goToShopify, goToShopifyCollection } from "@/lib/shopify";

/**
 * UNIXORN Footwear — Celestial Cream™ Low launch landing.
 * Museum-grade single-product presentation.
 */
export default function FootwearPage() {
  const [revealed, setRevealed] = useState(false);
  const [activeImg, setActiveImg] = useState("main");

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setRevealed(true), 80);
    return () => clearTimeout(t);
  }, []);

  const gallery = [
    { key: "side", src: p.images.side, label: "Side Profile" },
    { key: "front", src: p.images.front, label: "Front" },
    { key: "back", src: p.images.back, label: "Back" },
    { key: "inside", src: p.images.inside, label: "Inside" },
    { key: "sole", src: p.images.sole, label: "Sole" },
    { key: "main", src: p.images.main, label: "Hero" },
  ];

  return (
    <div className="min-h-screen bg-[#F4EFE6] relative overflow-x-hidden">
      <Navigation />

      {/* ───────── HERO ───────── */}
      <section
        className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden"
        data-testid="celestial-hero"
      >
        {/* soft radial light */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(255,251,240,0.95) 0%, rgba(244,239,230,0) 60%)",
          }}
        />
        <div className="container-luxury relative z-10">
          <div
            className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${
              revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-6">
              UNIXORN — First Footwear Release
            </p>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-stone-900 tracking-tight leading-[0.95] mb-7">
              CELESTIAL<br />CREAM<sup className="text-2xl md:text-3xl lg:text-4xl align-top ml-1">™</sup>
            </h1>
            <p className="text-lg md:text-2xl text-stone-600 italic max-w-2xl mx-auto mb-2">
              &ldquo;{p.tagline}&rdquo;
            </p>
          </div>

          {/* Hero product image — large, gallery-style */}
          <div
            className={`mt-16 md:mt-24 max-w-5xl mx-auto transition-all duration-1200 ${
              revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
            style={{ transitionDelay: "300ms" }}
          >
            <div className="relative aspect-[5/4] md:aspect-[16/10] overflow-hidden bg-[#F5F0E5] shadow-[0_60px_140px_-40px_rgba(120,100,72,0.35)]">
              <img
                src={p.images.main}
                alt={p.name}
                className="w-full h-full object-contain transition-transform duration-[1400ms] ease-out hover:scale-[1.02]"
                data-testid="celestial-hero-image"
              />
              {/* edition mark */}
              <div className="absolute top-6 left-6 px-4 py-2 bg-[#F4EFE6]/85 backdrop-blur-sm border border-stone-400/40">
                <span className="text-[10px] tracking-[0.3em] uppercase text-stone-700 font-semibold">
                  Inaugural Release · MMXXV
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── CAMPAIGN BANNER ───────── */}
      <section className="bg-[#EBE3D2] border-y border-stone-300/50">
        <div className="container-luxury py-14 md:py-20">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-center">
            <span className="hidden md:block h-px w-20 bg-stone-500/40" />
            <p className="text-xs md:text-sm tracking-[0.5em] uppercase text-stone-700 font-semibold">
              {p.campaign} — A NEW UNIXORN ERA
            </p>
            <span className="hidden md:block h-px w-20 bg-stone-500/40" />
          </div>
          <p className="text-center text-stone-600 text-base md:text-lg italic mt-6 max-w-2xl mx-auto">
            The first true UNIXORN footwear artifact.
          </p>
        </div>
      </section>

      {/* ───────── SPOTLIGHT ───────── */}
      <section className="section-padding bg-[#F4EFE6] relative">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-center max-w-6xl mx-auto">
            <div className="relative aspect-square overflow-hidden bg-[#F5F0E5] shadow-[0_40px_100px_-40px_rgba(120,100,72,0.3)]">
              <img
                src={p.images.side}
                alt="Celestial Cream Low — side profile"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="text-center md:text-left">
              <p className="text-[11px] tracking-[0.35em] uppercase text-stone-500 mb-5">
                The Silhouette
              </p>
              <h2 className="text-3xl md:text-5xl font-display font-bold text-stone-900 tracking-tight mb-8 leading-tight">
                Quiet luxury,<br />carried forward.
              </h2>
              <p className="text-stone-700 text-base md:text-lg leading-relaxed mb-10">
                {p.story}
              </p>

              <div className="flex flex-col sm:flex-row items-center md:items-start gap-5 mb-10">
                <p className="text-3xl md:text-4xl text-stone-900 font-semibold">
                  ${p.price}
                </p>
                <span className="hidden sm:block h-px w-10 bg-stone-400/60 sm:mt-7" />
                <p className="text-stone-500 text-sm italic sm:mt-8">
                  Limited inaugural release · Ships worldwide
                </p>
              </div>

              <Button
                onClick={() => goToShopify(p.id)}
                className="px-12 py-7 bg-stone-900 text-[#F4EFE6] hover:bg-stone-800 transition-all duration-500 rounded-none uppercase tracking-[0.35em] font-bold text-sm shadow-2xl"
                data-testid="celestial-acquire-btn"
              >
                Acquire The Form
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── GALLERY ───────── */}
      <section className="section-padding bg-[#EBE3D2] relative">
        <div className="container-luxury">
          <div className="text-center mb-14 md:mb-20">
            <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-5">
              UNIXORN — Detail Gallery
            </p>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-stone-900 tracking-tight">
              EVERY ANGLE A RELIC
            </h2>
          </div>

          {/* Active large frame */}
          <div className="max-w-4xl mx-auto mb-10">
            <div className="relative aspect-[4/3] overflow-hidden bg-[#F5F0E5] shadow-[0_40px_100px_-40px_rgba(120,100,72,0.3)] transition-all duration-700">
              <img
                src={p.images[activeImg]}
                alt={`Celestial Cream Low — ${activeImg}`}
                className="w-full h-full object-contain transition-opacity duration-500"
                key={activeImg}
                data-testid={`celestial-gallery-image-${activeImg}`}
              />
              <div className="absolute bottom-5 left-5 px-3.5 py-2 bg-[#F4EFE6]/85 backdrop-blur-sm border border-stone-400/40">
                <span className="text-[10px] tracking-[0.3em] uppercase text-stone-700 font-semibold">
                  {gallery.find((g) => g.key === activeImg)?.label || ""}
                </span>
              </div>
            </div>
          </div>

          {/* Thumbnail row */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4 max-w-4xl mx-auto">
            {gallery.map((g) => (
              <button
                key={g.key}
                onClick={() => setActiveImg(g.key)}
                className={`relative aspect-square overflow-hidden bg-[#F5F0E5] transition-all duration-300 ${
                  activeImg === g.key
                    ? "ring-2 ring-stone-900 ring-offset-2 ring-offset-[#EBE3D2]"
                    : "opacity-70 hover:opacity-100"
                }`}
                aria-label={g.label}
                data-testid={`celestial-thumb-${g.key}`}
              >
                <img src={g.src} alt={g.label} className="w-full h-full object-contain" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── SPEC ───────── */}
      <section className="section-padding bg-[#F4EFE6] relative">
        <div className="container-luxury max-w-3xl">
          <div className="text-center mb-14">
            <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-5">
              The Spec Sheet
            </p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-stone-900 tracking-tight">
              CONSTRUCTED. NOT MANUFACTURED.
            </h2>
          </div>

          <ul className="divide-y divide-stone-300/60 border-y border-stone-300/60">
            {p.spec.map((s) => (
              <li key={s.label} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-6 py-5">
                <span className="text-[11px] tracking-[0.35em] uppercase text-stone-500 font-semibold">
                  {s.label}
                </span>
                <span className="text-stone-800 text-base sm:text-right">{s.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───────── FINAL CTA ───────── */}
      <section className="bg-[#EBE3D2] border-t border-stone-300/50">
        <div className="container-luxury py-20 md:py-28 text-center">
          <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-6">
            Worn at the foundation. Carried forward.
          </p>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-stone-900 tracking-tight mb-10">
            ENTER THE CELESTIAL ERA
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => goToShopify(p.id)}
              className="px-12 py-7 bg-stone-900 text-[#F4EFE6] hover:bg-stone-800 transition-all duration-500 rounded-none uppercase tracking-[0.35em] font-bold text-sm"
              data-testid="celestial-final-acquire"
            >
              Acquire — ${p.price}
            </Button>
            <Button
              variant="outline"
              onClick={() => goToShopifyCollection("footwear")}
              className="px-12 py-7 border border-stone-700/40 text-stone-700 hover:bg-stone-900 hover:text-[#F4EFE6] hover:border-stone-900 transition-all duration-500 rounded-none uppercase tracking-[0.35em] font-bold text-sm bg-transparent"
            >
              View Collection
            </Button>
          </div>
          <p className="text-stone-500 text-xs italic mt-10">
            Secure checkout on UNIXORN · Ships worldwide
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
